import { useCallback, useEffect, useState } from 'react'
import type { JSX } from 'react'

import { SALE_TYPES, type SaleType } from '@lensmira/domain'
import { formatDisplayDate, formatDisplayTime } from '@lensmira/i18n'

import { ReadFailed, type OpenDispense } from '../api/reads.js'
import { Ltr, useTranslation } from '../i18n.js'
import { statusLine } from '../queue/status-line.js'
import { useWriteSettled } from '../queue/use-settled-write.js'
import { useWriteQueue } from '../queue/use-write-queue.js'
import { sentence } from '../sentence.js'
import { DataSurface } from './DataSurface.js'
import { saleTypeLabel, saleTypeWord } from '../status-labels.js'

/**
 * Incomplete dispenses, and resuming one. PRD §6.6 `DSP-005`.
 *
 * > "saved incomplete and resumed by another staff member on another terminal"
 *
 * **This is the resumability half of §6.6 and not the dispense screen itself.** §6.6 asks for "one
 * screen, multi-pair, inline warnings", which is the lens-choosing surface and needs the selection
 * service, the availability rules and the pricing explanation beside each pair. That does not
 * exist yet and this does not pretend to be it: what the API implements today is a draft with an
 * opaque `choices` blob, a customer, a prescription, a sale type and a version, and that is exactly
 * what this screen manages. Inventing a shape for `choices` here would be designing the dispense
 * UI through its storage, which is the wrong end.
 *
 * **The list is per branch, not per person**, and that is the requirement rather than a
 * convenience. A customer who comes back at four o'clock is served by whoever is on the floor, and
 * a draft only its author can see is a draft that gets started again from nothing. So the screen
 * leads with who saved it and when: picking up somebody's half-finished sale without knowing whose
 * it is, is how two people end up serving the same customer.
 *
 * **Saving carries the version it read.** `ARC-007` makes a stale write a conflict with the
 * current state rather than a silent overwrite, and two terminals on one draft is the case
 * `DSP-005` invites, so the conflict is the ordinary path here rather than the exceptional one.
 * The draft is re-read once the server has taken a save, because otherwise the second save of a
 * sitting sends the version the first one superseded and this screen conflicts with itself.
 *
 * **Times are the branch's, not the browser's.** `BR-01` and Appendix J: a tablet in a shop can
 * have any clock at all, and Pakistan is UTC+5, so slicing the ISO string would tell a shopkeeper
 * a draft was left five hours before it was. This is the first consumer of `formatDisplayTime`.
 */

export interface DispensingProps {
  readonly branchId: string
  /** `BR-01`: the branch's own zone, from the session context. Never the browser's. */
  readonly timezone: string
  readonly loadOpen: () => Promise<readonly OpenDispense[]>
  readonly loadOne: (dispenseId: string) => Promise<OpenDispense>
  /** Carried from Customers, the way `RxCapture` and `NewOrder` carry them. */
  readonly customerId?: string
  readonly customerName?: string
  readonly prescriptionId?: string
  readonly onResumed?: () => void
}

export function Dispensing({
  branchId,
  timezone,
  loadOpen,
  loadOne,
  customerId,
  customerName,
  prescriptionId,
  onResumed,
}: DispensingProps): JSX.Element {
  const { t, locale } = useTranslation()
  const { enqueue, snapshot } = useWriteQueue()

  const [open, setOpen] = useState<readonly OpenDispense[] | null>(null)
  const [loadError, setLoadError] = useState<string | null>(null)
  // Separate from `loadError`, which is the resume line: a failed Carry-on click must not replace
  // the whole list with an error, and a failed list read must not read as a resume failure.
  const [listError, setListError] = useState<string | null>(null)
  const [resumed, setResumed] = useState<OpenDispense | null>(null)
  const [saleType, setSaleType] = useState<SaleType | ''>('')
  const [writeId, setWriteId] = useState<string | null>(null)

  const refresh = useCallback(async () => {
    try {
      setOpen(await loadOpen())
      setListError(null)
    } catch (error) {
      setListError(error instanceof ReadFailed ? error.message : t('dispensing.loadFailed'))
    }
  }, [loadOpen, t])

  useEffect(() => {
    void refresh()
  }, [refresh])

  // A write settling is what makes the list right again: the draft that was saved has a new
  // version, and another terminal may have added one since.
  useWriteSettled(snapshot, writeId, () => {
    setWriteId(null)
    void refresh()
    // The resumed draft too, and this is not belt-and-braces: the save it just made moved the
    // version, so without this the next click sends the one the server has already superseded and
    // the screen conflicts with its own previous save.
    if (resumed) void resume(resumed.id)
    onResumed?.()
  })

  const resume = useCallback(
    async (dispenseId: string) => {
      try {
        const draft = await loadOne(dispenseId)
        setResumed(draft)
        setSaleType((draft.saleType ?? '') as SaleType | '')
        setLoadError(null)
      } catch (error) {
        setLoadError(error instanceof ReadFailed ? error.message : t('dispensing.loadFailed'))
      }
    },
    [loadOne, t],
  )

  const start = useCallback(async () => {
    const write = (await enqueue({
      method: 'POST',
      path: '/dispenses',
      label: t('dispensing.queue.startLabel'),
      body: {
        branchId,
        ...(customerId === undefined ? {} : { customerId }),
        ...(prescriptionId === undefined ? {} : { prescriptionId }),
        ...(saleType === '' ? {} : { saleType }),
        choices: {},
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [branchId, customerId, prescriptionId, saleType, enqueue, t])

  const save = useCallback(async () => {
    if (!resumed) return
    const write = (await enqueue({
      method: 'PUT',
      path: `/dispenses/${resumed.id}`,
      label: t('dispensing.queue.saveLabel'),
      body: {
        // The version this screen read. A save from another terminal in between is a conflict,
        // which `ARC-007` makes a message rather than an overwrite.
        expectedVersion: resumed.version,
        ...(resumed.customerId === undefined ? {} : { customerId: resumed.customerId }),
        ...(resumed.prescriptionId === undefined ? {} : { prescriptionId: resumed.prescriptionId }),
        ...(saleType === '' ? {} : { saleType }),
        // The whole saved state: a field left out is cleared, which the service says in terms.
        choices: resumed.choices,
      },
    })) as { id: string }
    setWriteId(write.id)
  }, [resumed, saleType, enqueue, t])

  const when = (iso: string): string => {
    const instant = Date.parse(iso)
    if (!Number.isFinite(instant)) return iso
    return `${formatDisplayDate(instant, timezone, locale)} ${formatDisplayTime(instant, timezone, locale)}`
  }

  const line = statusLine(t, snapshot, writeId, t('queue.subject.dispense'))

  return (
    <section className="lm-screen" aria-labelledby="dispensing-heading">
      <h1 id="dispensing-heading" className="lm-screen__heading">
        {t('dispensing.heading')}
      </h1>

      <p className="lm-hint">{t('dispensing.whatThisIs')}</p>

      {loadError === null ? null : (
        <p className="lm-hint" role="alert">
          {loadError}
        </p>
      )}

      <div className="lm-form">
        <label className="lm-field lm-field--inline">
          <span className="lm-field__label">{t('dispensing.saleTypeLabel')}</span>
          <select
            className="lm-input"
            value={saleType}
            onChange={(e) => setSaleType(e.currentTarget.value as SaleType | '')}
          >
            <option value="">{t('dispensing.saleTypeNone')}</option>
            {SALE_TYPES.map((type) => (
              <option key={type} value={type}>
                {saleTypeLabel(t, type)}
              </option>
            ))}
          </select>
        </label>

        <button type="button" className="lm-button lm-button--primary" onClick={() => void start()}>
          {customerName === undefined
            ? t('dispensing.startWithoutCustomer')
            : t('dispensing.start', { name: customerName })}
        </button>
      </div>

      {line === null ? null : (
        <p className="lm-queue-state" role="status">
          {line}
        </p>
      )}

      <h2 className="lm-screen__subheading">{t('dispensing.openHeading')}</h2>

      {/*
        An empty list here is the good outcome, not a first-run one: nothing is half-done. A refresh
        that fails after the list has loaded keeps the stale list rather than blanking it.
      */}
      <DataSurface
        state={
          listError !== null && open === null
            ? { kind: 'error', message: listError, onRetry: () => void refresh() }
            : open === null
              ? { kind: 'loading' }
              : open.length === 0
                ? { kind: 'empty' }
                : { kind: 'ready' }
        }
        shape="rows"
        rows={3}
        what={t('dispensing.what')}
        emptyMessage={t('dispensing.empty')}
      >
        <ul className="lm-dispense-list">
          {(open ?? []).map((draft) => (
            <li key={draft.id} className="lm-dispense-row">
              <span className="lm-dispense-row__who">
                {/* Who left it and when, first: that is what tells a colleague whether to pick it
                    up or leave it for the person serving that customer. */}
                {sentence(t, 'dispensing.savedBy', {
                  // The name when the server resolved one. The id is the fallback and not the
                  // intent: it is there so a draft saved by the seeder still says something
                  // traceable rather than nothing.
                  staff: <Ltr>{draft.savedByName ?? draft.savedByStaffId}</Ltr>,
                  when: <Ltr>{when(draft.savedAt)}</Ltr>,
                })}
              </span>
              <span className="lm-dispense-row__what">
                {draft.saleType === undefined
                  ? t('dispensing.noSaleType')
                  : saleTypeWord(t, draft.saleType)}
              </span>
              <button
                type="button"
                className="lm-button lm-button--secondary"
                onClick={() => void resume(draft.id)}
              >
                {t('dispensing.resume')}
              </button>
            </li>
          ))}
        </ul>
      </DataSurface>

      {resumed === null ? null : (
        <div className="lm-form">
          <h2 className="lm-screen__subheading">{t('dispensing.resumedHeading')}</h2>
          <p className="lm-hint">
            {sentence(t, 'dispensing.resumedVersion', {
              version: <Ltr>{resumed.version}</Ltr>,
            })}
          </p>
          <button
            type="button"
            className="lm-button lm-button--primary"
            onClick={() => void save()}
          >
            {t('dispensing.save')}
          </button>
        </div>
      )}
    </section>
  )
}
